<script setup lang="ts">
import LeHeading from "@dsk/components/Heading/LeHeading.vue";
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeCross from "@dsk/icons/LeCross.vue";
import LeThumbsDown from "@dsk/icons/LeThumbsDown.vue";
import LeThumbsUp from "@dsk/icons/LeThumbsUp.vue";
import { Dialog as DialogElement, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from "@headlessui/vue";
import {
  type Fn,
  useElementBounding,
  type UseElementBoundingReturn,
  useEventListener,
  useScrollLock,
  useWindowScroll,
  type UseWindowScrollOptions,
  useWindowSize
} from "@vueuse/core";
import { ref, watch, type WatchHandle } from "vue";
import { useI18n } from "vue-i18n";

import { sleep } from "@/utils";

interface Props {
  isOpen: boolean;
  placement?: "left" | "right";
  tag?: "div" | "span";
  padding?: number;
}

const props = withDefaults(defineProps<Props>(), {
  placement: "left",
  tag: "div",
  padding: 0
});

const emit = defineEmits<{
  rate: [value: boolean];
  close: [];
}>();

const onClose = () => {
  isShown.value = false;
  emit("close");
};

const onRate = (rating: boolean) => {
  ratingGiven.value = rating;
  emit("rate", rating);
};

const { t } = useI18n();

const isShown = ref<boolean>(false);
const ratingGiven = ref<boolean | null>(null);

const highlight = ref<HTMLElement | null>(null);

type HighlighttPlacement = {
  top: number;
  left: number;
  width: number;
  height: number;
};

const highlightPlacement = ref<HighlighttPlacement>({
  top: 0,
  left: 0,
  width: 0,
  height: 0
});

const modal = ref<HTMLElement | null>(null);

type ModalPlacement = {
  top: number;
  left: number;
};

const modalPlacement = ref<ModalPlacement>({
  top: 0,
  left: 0
});

const { width: windowWidth, height: windowHeight } = useWindowSize();

const TIMEOUT_AFTER_RATING = 750;
const MODAL_WIDTH = 320;
const MODAL_HEIGHT = 250;
const MODAL_TOP_MARGIN = 5;
const HEIGHT_BUFFER = 25;

const scrollToModal = (highlightBounding: UseElementBoundingReturn, state: "initial" | "resize") => {
  const top = highlightBounding.top.value - HEIGHT_BUFFER;
  const bottom = highlightBounding.bottom.value + MODAL_HEIGHT + HEIGHT_BUFFER;

  let scrollToY: number | null = null;
  let options: UseWindowScrollOptions | undefined = undefined;

  // Scroll should be smooth when we open the modal
  if (state === "initial") {
    options = { behavior: "smooth" };
  }

  const { isScrolling, y } = useWindowScroll(options);

  if (top < 0) {
    scrollToY = window.scrollY + top;
  } else if (bottom > windowHeight.value) {
    scrollToY = y.value + bottom - windowHeight.value;
  } else {
    isShown.value = true;
    return;
  }

  y.value = scrollToY;

  const isScrollingWatchHandler = watch(
    () => isScrolling.value,
    (isStillScrolling) => {
      if (isStillScrolling) {
        return;
      }

      isShown.value = true;
      isScrollingWatchHandler();
    }
  );
};

const calculatePlacement = (highlightBounding: UseElementBoundingReturn) => {
  let modalLeft = 0;
  if (props.placement === "left") {
    modalLeft = highlightBounding.left.value - props.padding;
  } else {
    modalLeft = highlightBounding.right.value - MODAL_WIDTH + props.padding;
  }

  modalPlacement.value = {
    top: highlightBounding.bottom.value + MODAL_TOP_MARGIN + props.padding / 2,
    left: modalLeft
  };

  highlightPlacement.value = {
    top: highlightBounding.top.value - props.padding / 2,
    left: highlightBounding.left.value - props.padding,
    width: highlightBounding.width.value + props.padding * 2,
    height: highlightBounding.height.value + props.padding
  };
};

let windowSizeWatchHandler: WatchHandle | null = null;

const watchWindowSize = (highlightBounding: UseElementBoundingReturn) => {
  windowSizeWatchHandler = watch([windowWidth, windowHeight], () => {
    scrollToModal(highlightBounding, "resize");
  });
};
const unwatchWindowSize = () => {
  if (windowSizeWatchHandler) {
    windowSizeWatchHandler();
    windowSizeWatchHandler = null;
  }
};

let highlightBoundingWatchHandler: WatchHandle | null = null;

const watchHighlightBounding = (highlightBounding: UseElementBoundingReturn) => {
  highlightBoundingWatchHandler = watch(
    [
      highlightBounding.top,
      highlightBounding.bottom,
      highlightBounding.left,
      highlightBounding.width,
      highlightBounding.height
    ],
    () => {
      calculatePlacement(highlightBounding);
    },
    { immediate: true }
  );
};
const unwatchHighlightBounding = () => {
  if (highlightBoundingWatchHandler) {
    highlightBoundingWatchHandler();
    highlightBoundingWatchHandler = null;
  }
};

let keyEventHandlerCleanUpFn: Fn | null = null;

const listenToEscapeKey = () => {
  keyEventHandlerCleanUpFn = useEventListener(window, "keydown", (event) => {
    if (event.key === "Escape") {
      onClose();
    }
  });
};
const unlistenToEscapeKey = () => {
  if (keyEventHandlerCleanUpFn) {
    keyEventHandlerCleanUpFn();
    keyEventHandlerCleanUpFn = null;
  }
};

const isLocked = useScrollLock(window);

const lockScroll = () => {
  // Inspired by Headless UIs adjustScrollbarPadding hook, which unfortunately is not exposed
  const htmlElement = document.documentElement;
  const widthBeforeLock = Math.max(0, window.innerWidth - htmlElement.clientWidth);
  const widthAfterLock = Math.max(0, htmlElement.clientWidth - htmlElement.offsetWidth);
  const scrollWidth = Math.max(0, widthBeforeLock - widthAfterLock);

  isLocked.value = true;
  document.documentElement.style.paddingRight = `${scrollWidth}px`;
};
const unlockScroll = () => {
  isLocked.value = false;
  document.documentElement.style.paddingRight = "";
};

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      lockScroll();

      const highlightBounding = useElementBounding(highlight);

      watchWindowSize(highlightBounding);
      watchHighlightBounding(highlightBounding);
      listenToEscapeKey();

      ratingGiven.value = null;
      scrollToModal(highlightBounding, "initial");

      return;
    }

    unwatchWindowSize();
    unwatchHighlightBounding();
    unlistenToEscapeKey();

    unlockScroll();
  }
);

const submitRating = async (rating: boolean) => {
  onRate(rating);
  await sleep(TIMEOUT_AFTER_RATING);
  onClose();
};
</script>

<template>
  <component :is="props.tag" ref="highlight">
    <slot />

    <TransitionRoot :show="isShown" as="template">
      <DialogElement
        tabindex="-1"
        class="le-feedback-highlight"
        :style="{
          top: `${modalPlacement.top}px`,
          left: `${modalPlacement.left}px`
        }"
      >
        <TransitionChild
          as="template"
          enter="fade-enter-active"
          enter-from="fade-enter-from"
          leave="fade-leave-active"
          leave-to="fade-leave-to"
        >
          <div
            class="le-feedback-highlight__backdrop"
            aria-hidden="true"
            :style="{
              top: `${highlightPlacement.top}px`,
              left: `${highlightPlacement.left}px`,
              width: `${highlightPlacement.width}px`,
              height: `${highlightPlacement.height}px`
            }"
          />
        </TransitionChild>

        <TransitionChild
          as="template"
          enter="fade-enter-active"
          enter-from="fade-enter-from"
          leave="fade-leave-active"
          leave-to="fade-leave-to"
        >
          <div class="le-feedback-highlight__dialog">
            <DialogPanel ref="modal" class="le-feedback-highlight-panel" :style="{ width: `${MODAL_WIDTH}px` }">
              <DialogTitle class="le-feedback-highlight-panel__title" as="header">
                <LeHeading tag="h1" size="s">{{ t("le-dicto.feedback.titel") }}</LeHeading>

                <LeIconButton :aria-label="t('le-dicto.feedback.close-modal')" color="primary" @click="onClose()">
                  <LeCross role="presentation" />
                </LeIconButton>
              </DialogTitle>

              <section class="le-feedback-highlight-panel__body">
                <div class="le-feedback-highlight-panel__icon-wrapper">
                  <LeIconButton
                    v-if="ratingGiven !== false"
                    :aria-label="t('le-dicto.feedback.thumbs-up')"
                    class="le-feedback-highlight-panel__icon-button"
                    size="xl"
                    color="primary"
                    variant="dark"
                    :disabled="ratingGiven === true"
                    @click="submitRating(true)"
                  >
                    <LeThumbsUp class="le-feedback-highlight-panel__icon" role="presentation" />
                  </LeIconButton>

                  <LeIconButton
                    v-if="ratingGiven !== true"
                    :aria-label="t('le-dicto.feedback.thumbs-down')"
                    class="le-feedback-highlight-panel__icon-button"
                    size="xl"
                    color="error"
                    variant="dark"
                    :disabled="ratingGiven === false"
                    @click="submitRating(false)"
                  >
                    <LeThumbsDown class="le-feedback-highlight-panel__icon" role="presentation" />
                  </LeIconButton>
                </div>
                <p class="le-feedback-highlight-panel__body-text">
                  <template v-if="ratingGiven === null">{{ t("le-dicto.feedback.text.description") }}</template>
                  <template v-else>{{ t("le-dicto.feedback.text.confirm") }}</template>
                </p>
              </section>
            </DialogPanel>
          </div>
        </TransitionChild>
      </DialogElement>
    </TransitionRoot>
  </component>
</template>

<style scoped lang="less">
@import "@dsk/styles/all.less";

@modal-z-index: 1040;
@modal-backdrop-z-index: 1;
@modal-dialog-z-index: 2;

// For some reason .le-feedback-highlight doesn't get a v-scope-id and therefore unable to
// set v-bind values for the placement properties. So we have to use the :style attribute
:global(.le-feedback-highlight) {
  display: flex;
  position: fixed;
  z-index: @modal-z-index;
}

.le-feedback-highlight {
  &__backdrop {
    position: fixed;
    z-index: @modal-backdrop-z-index;
    inset: 0;

    &::after {
      content: "";
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 4px;
      box-shadow: 0px 0px 0px 9999px rgba(0, 0, 0, 0.3);
    }
  }

  &__dialog {
    z-index: @modal-dialog-z-index;
    box-shadow: 0px 11px 12px -7px rgba(32, 43, 33, 0.25);
  }

  &-panel {
    overflow-y: auto;
    border-radius: 4px;

    &__title,
    &__body {
      padding: 20px 24px;
    }

    &__title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 75px;
      background: @colors[@neutrals][100];

      .le-heading {
        margin-right: 10px;
        color: @colors[@neutrals][400];
      }
    }

    &__body {
      .body-text("s");
      display: flex;
      flex-direction: column;
      align-items: center;
      background: @colors[@secondary][100];
      color: @colors[@neutrals][500];
      gap: 16px;

      &-text {
        margin: 0;
        text-align: center;
      }
    }

    &__icon-wrapper {
      display: flex;
      gap: 16px;
    }

    &__icon-button {
      display: flex;
      justify-content: center;
    }

    &__icon {
      width: 22px;
      height: 22px;
    }
  }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 75ms ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
